import { Component, OnInit, Inject } from '@angular/core';
import { IncidentWitnessModel } from '../incident-witness.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UtilsService } from 'src/app/shared/utils.service';
import { NetworkedUsersSelectorComponent } from 'src/app/shared/networked-users-selector/networked-users-selector.component';
import { FileUploaderService } from 'src/app/shared/file-uploader.service';
import { IncidentWitnessService } from '../incident-witness.service';
import { UserService } from 'src/app/shared/user.service';
import { FilesRemoveComponent } from 'src/app/files/files-remove/files-remove.component';
import { UserModel } from '../../models/user.model';
import { ApiRequestService } from '../../shared/api-request.service';

@Component({
  selector: 'app-incidents-witnesses-edit',
  templateUrl: './incidents-witnesses-edit.component.html',
  styleUrls: ['./incidents-witnesses-edit.component.scss']
})
export class IncidentsWitnessesEditComponent implements OnInit {
  witness = {} as IncidentWitnessModel;
  inProgress = false;

  selectedFiles: File[] = [];

  // Mobile field Country Code selector values
  phone_input_id: string = 'phone-1';
  phone_number: string = '';
  phone_country_code: string = '';
  phone_dial_code: string = '';
  phone_e164: string = '';
  phone_error_state: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private data: { witnessId: number | string; incidentId: number },
    public utils: UtilsService,
    private dialog: MatDialogRef<IncidentsWitnessesEditComponent>,
    private fileUploaderService: FileUploaderService,
    private witnessService: IncidentWitnessService,
    private api: ApiRequestService
  ) {}

  ngOnInit() {
    this.witness.id = Number(this.data.witnessId);
    this.witness.incident_id = this.data.incidentId;
    if (isNaN(this.witness.id)) {
      return;
    }
    this.witnessService
      .find(this.witness.id)
      .subscribe((response) => {
        this.witness = response.data;
        this.initPhoneDetails();
      });
  }

  /**
   * Get Output values from phone input component.
   */
  getOutputPhoneDetails($event) {
    if($event) {
      let phone = $event;
      this.phone_number = phone.hasOwnProperty('number') && phone.number !== null ? phone.number : '';
      this.phone_country_code = phone.hasOwnProperty('countryCode') && phone.countryCode !== null ? phone.countryCode : '';
      this.phone_dial_code = phone.hasOwnProperty('dialCode') && phone.dialCode !== null ? phone.dialCode : '';
      this.phone_e164 = phone.hasOwnProperty('e164Number') && phone.e164Number !== null ? phone.e164Number : '';
      this.phone_error_state = phone.hasOwnProperty('errorState') && phone.errorState !== null ? phone.errorState : true;
    }
  }

  /**
   * Set Input values for phone input component.
   */
  private initPhoneDetails() {
    this.phone_number = this.witness.phone;
    this.phone_country_code = this.witness.phone_country_code;
    this.phone_dial_code = this.witness.phone_dial_code;
    this.phone_e164 = this.witness.phone_e164;
  }

  onRemoveFiles() {
    this.utils.showComponentDialog(
      FilesRemoveComponent,
      this.witness.files,
      {
        width: '700px'
      },
      () => {
        this.ngOnInit();
      }
    );
  }

  /**
   * Display a component dialog to select a witness user and update the witness details.
   * The most recent update to this fixed a security flaw where sensitive data was shown.
   *
   * @return {void}
   */
  onSelectWitness(): void {
    this.utils.showComponentDialog(NetworkedUsersSelectorComponent, {
        multiple: false,
        selected: [this.witness.user_id],
        visitors_from_all_sites: true,
        return_objects: true
      }, {
        width: '1200px'
      }, (witness: UserModel): void => {
        // Terminate early if no witness was selected.
        if ( typeof witness === 'undefined' ) {
          return;
        }
        // Store the selected user id.
        this.witness.user_id = witness.id;
        // Store the rest of the witness details.
        this.witness.full_name = witness.contact_person;
        this.witness.email = witness.email;
        this.witness.phone = witness.mobile;
        this.witness.phone_country_code = witness.mobile_country_code;
        this.witness.phone_dial_code = witness.mobile_dial_code;
        this.witness.phone_e164 = witness.mobile_e164;
        this.witness.company_name = witness.company_name;
      });
  }

  onDelete() {
    this.utils.showModal(
      'Delete Witness',
      'Are you sure you want to delete this witness?',
      () => {
        this.witnessService.remove([this.witness.id]).subscribe(() => {
          this.utils.showToast('Deleted witness');
          this.dialog.close(true);
        });
      }
    );
  }

  onSubmit() {
    // Check Error states of phone inputs then continue submission
    if(this.phone_error_state) {
      this.utils.showModal('Form Validation Failed','A Phone number must be at least 3 characters long.');
      return;
    }

    // Terminate early if another process is in progress.
    if (this.inProgress) {
      return;
    }

    // Update User details with Mobile field Country Code selector values
    this.witness.phone = this.phone_number;
    this.witness.phone_country_code = this.phone_country_code;
    this.witness.phone_dial_code = this.phone_dial_code;
    this.witness.phone_e164 = this.phone_e164;

    this.inProgress = true;
    if (isNaN(this.witness.id)) {
      this.create();
    } else {
      this.update();
    }
  }

  /**
   * Handles the creation of a new witness by interacting with the witness service.
   * After successful creation, displays a confirmation toast message, uploads selected files,
   * and closes the dialog. In case of an error, it resets the progress flag.
   *
   * @return {void} Does not return a value.
   */
  private create(): void {
    this.witnessService.create(this.witness).subscribe((response): void => {
      this.utils.showToast('Created a Witness');
      this.uploadSelectedFiles(response.data.id, (): void => {
        this.dialog.close(true);
        this.inProgress = false;
      })
    }, (_error: any): void => {
      this.inProgress = false;
    });
  }

  /**
   * Updates the witness information by calling the witnessService's update method.
   * Upon successful update, shows a toast notification, resets associated files,
   * reinitializes the component, and marks the update process as complete.
   *
   * @return {void} Does not return a value.
   */
  private update(): void {
    this.witnessService.update(this.witness).subscribe((response): void => {
      this.utils.showToast('Updated a Witness');
      this.ngOnInit();
      this.inProgress = false;
    });
  }

  /**
   * Handles the selection of files and appends them to the selected files list.
   *
   * @param {FileList} fileList - The list of files selected by the user.
   * @return {void} This method does not return any value.
   */
  onSelectFiles(fileList: FileList): void {
    const files: File[] = Array.from(fileList);
    if ( files && files.length > 0 ) {
      this.selectedFiles.push(...files);
    }
  }

  /**
   * Uploads the selected files related to an incident witness using the given ID and makes an API request.
   * After the upload is completed, an optional callback function is executed.
   *
   * @param {number} id - The ID related to the incident witness for file upload.
   * @param {any} [callback] - An optional callback function to execute after the upload process is completed.
   * @return {void} This method does not return a value.
   */
  private uploadSelectedFiles(id: number, callback?: any): void {
    if ( id && this.selectedFiles.length > 0 ) {
      this.api.makeUploadRequest(`v2/file-manager/incident-witness/${id}`, this.selectedFiles)
        .then((response: any): void => {
          this.utils.showToast('Your files successfully uploaded.');
        })
        .finally((): void => {
          this.selectedFiles.length = 0;
          if ( typeof callback === 'function' ) {
            callback();
          }
        });
    }
  }
}
